import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cake, PartyPopper, Gift, Star, Calendar } from 'lucide-react';
import { differenceInDays, differenceInHours, differenceInMinutes, format } from 'date-fns';

interface Props {
  foundingDate?: Date;
  clubName?: string;
}

export const ClubAnniversary = ({ 
  foundingDate = new Date('2024-01-28'), // Default VSU founding date
  clubName = 'VSU' 
}: Props) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [isAnniversary, setIsAnniversary] = useState(false);
  const [yearsOld, setYearsOld] = useState(0);

  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      
      // Calculate next anniversary date
      let nextAnniversary = new Date(currentYear, foundingDate.getMonth(), foundingDate.getDate());
      
      // If anniversary has passed this year, use next year
      if (nextAnniversary < now) {
        nextAnniversary = new Date(currentYear + 1, foundingDate.getMonth(), foundingDate.getDate());
      }

      // Check if today is the anniversary
      const isToday = format(now, 'MM-dd') === format(foundingDate, 'MM-dd');
      setIsAnniversary(isToday);

      // Calculate years
      const years = currentYear - foundingDate.getFullYear() + (isToday ? 0 : 0);
      setYearsOld(now >= new Date(currentYear, foundingDate.getMonth(), foundingDate.getDate()) 
        ? currentYear - foundingDate.getFullYear() 
        : currentYear - foundingDate.getFullYear() - 1
      );

      // Calculate time left
      const days = differenceInDays(nextAnniversary, now);
      const hours = differenceInHours(nextAnniversary, now) % 24;
      const minutes = differenceInMinutes(nextAnniversary, now) % 60;

      setTimeLeft({ days, hours, minutes });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [foundingDate]);

  if (isAnniversary) {
    return (
      <Card className="p-4 bg-gradient-to-br from-yellow-500/20 via-primary/20 to-purple-500/20 border-yellow-500/30 overflow-hidden relative">
        {/* Confetti-like background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA'][i % 4],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`
              }}
            />
          ))}
        </div>

        <div className="relative text-center space-y-3">
          <div className="flex justify-center gap-2">
            <PartyPopper className="w-8 h-8 text-yellow-500 animate-bounce" />
            <Cake className="w-8 h-8 text-pink-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
            <PartyPopper className="w-8 h-8 text-yellow-500 animate-bounce" style={{ animationDelay: '0.4s', transform: 'scaleX(-1)' }} />
          </div>
          
          <div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              🎉 Happy {yearsOld + 1}th Anniversary! 🎉
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {clubName} আজ {yearsOld + 1} বছর পূর্ণ করছে!
            </p>
          </div>

          <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
            <Gift className="w-3 h-3 mr-1" />
            বিশেষ দিন
          </Badge>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-primary" />
        <h3 className="font-bold text-lg">Club Anniversary</h3>
      </div>

      <div className="text-center space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">পরবর্তী বার্ষিকী</p>
          <p className="text-lg font-semibold">
            {format(new Date(new Date().getFullYear() + (timeLeft.days <= 0 ? 1 : 0), foundingDate.getMonth(), foundingDate.getDate()), 'dd MMMM yyyy')}
          </p>
        </div>

        {/* Countdown */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-primary/10 rounded-lg p-3">
            <div className="text-2xl font-bold text-primary">{timeLeft.days}</div>
            <div className="text-xs text-muted-foreground">দিন</div>
          </div>
          <div className="bg-primary/10 rounded-lg p-3">
            <div className="text-2xl font-bold text-primary">{timeLeft.hours}</div>
            <div className="text-xs text-muted-foreground">ঘন্টা</div>
          </div>
          <div className="bg-primary/10 rounded-lg p-3">
            <div className="text-2xl font-bold text-primary">{timeLeft.minutes}</div>
            <div className="text-xs text-muted-foreground">মিনিট</div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Star className="w-4 h-4 text-yellow-500" />
          <span>{clubName} - {yearsOld} বছর পূর্ণ</span>
        </div>
      </div>
    </Card>
  );
};
